/* eslint-disable camelcase */
import React from 'react';
import {
  Box, Checkbox, Stack, styled, Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import metadataConfig from './metadata-config';
import { PhotoMediaItem } from '../image-dialog';
import Button from '../../../components/button';

export interface MetadataBuilder {
  record: Record<string, any>;
  photoMetadata: Record<string, any>;
}

export type ExifMetaDataItem = {
  label: string;
  hasField?: boolean;
  fieldKey?: string;
  value: (rawMetadata: MetadataBuilder) => string;
}

const StyledCheckBox = styled(Checkbox)(({
  margin: '6px 0px 0px -10px!important',
  padding: 0,
  '& .MuiSvgIcon-root': {
    fontSize: 16,
    borderRadius: 20,
  },
  '& .PrivateSwitchBase-input': {
    left: 'initial !important',
  },
}));

const StyledButton = styled(Button)(({
  width: 0,
  height: 0,
  padding: 0,
  marginTop: 11,
  marginLeft: 4,
  minWidth: 'unset',
}));

const StyledTitle = styled(Typography)({
  width: '116px',
  margin: '4px 0px',
  fontSize: 16,
  fontWeight: 700,
  lineHeight: '24px',
});

const StyledLabel = styled(Typography)({
  width: '116px',
  maxWidth: '116px',
  margin: '4px 0px',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: '20px',
});

const StyledValue = styled(Typography)({
  width: '230px',
  margin: '4px 0px',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '20px',
  maxHeight: '36px',
  overflow: 'hidden',
  '&:hover': {
    overflowY: 'scroll',
  },
});

const CaptionField = styled(TextField)(({
  border: '1px solid rgba(0, 0, 0, 0.23)!important',
  borderRadius: '4px',
  '& .MuiInputBase-root': {
    width: '325px',
    height: '100px',
    padding: '8px 12px',
    alignItems: 'flex-start',
    fontSize: '16px',
    overflow: 'hidden',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'unset',
  },
}));

interface Props {
  record: Record<string, any>;
  photoMetadata: Record<string, any>;
  photoMediaItem: PhotoMediaItem,
  onChange: (key: string | undefined, value: string | boolean) => void,
}

const PhotoMetadataViewer: React.FC<Props> = ({
  record, photoMetadata,
  photoMediaItem, onChange,
}) => {
  const { item } = photoMediaItem;
  const { showObjects, caption } = { ...item };
  const exif = photoMetadata.exif ? photoMetadata.exif : {};
  const metadataBuilder: MetadataBuilder = {
    record: record || {},
    photoMetadata: {
      ...photoMetadata,
      exif: { ...exif },
    },
  };

  return (
    <>
      <Box sx={{ width: '324px' }}>
        <Box key="_fields" sx={{ paddingBottom: '24px' }}>
          <StyledTitle>Metadata</StyledTitle>
          {
            metadataConfig.map((exifItem: ExifMetaDataItem) => (
              <Box data-testid={`test-${exifItem.label}`} key={`${exifItem.label}`}>
                <Stack direction="row" justifyContent="flex-start" alignItems="flex-start">
                  <StyledLabel>{exifItem.label}</StyledLabel>
                  { !exifItem.hasField
                    ? <StyledValue>{exifItem.value(metadataBuilder)}</StyledValue>
                    : (
                      <StyledButton
                        onClick={() => onChange(
                          exifItem.fieldKey,
                          !showObjects,
                        )}
                      >
                        <StyledCheckBox
                          checked={showObjects}
                        />
                      </StyledButton>
                    )}
                </Stack>
              </Box>
            ))
          }
        </Box>
        <Box key="_captions">
          <Stack direction="column">
            <StyledTitle>Caption</StyledTitle>
            <CaptionField
              multiline
              maxRows={8}
              value={caption || ''}
              placeholder="Write caption here"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(
                'caption',
                event.target.value,
              )}
              inputProps={{ 'data-testid': 'test-caption-field' }}
            />
          </Stack>
        </Box>
      </Box>
    </>
  );
};
export default PhotoMetadataViewer;
